import {
  BATTLE_MODE_COPY,
  BATTLE_PLAYER_COUNTS,
  type BattleCopy,
  type BattleCreateCopy,
  type BattleMode,
  type BoxSummary,
  defaultBattleCopy,
  defaultBattleCreateCopy,
  formatAmountWithUnit,
  MAX_BATTLE_ROUNDS,
  spendableBalanceLine,
} from "@loot-os/api-contract";
import type {
  BattleBoxPickContent,
  BattleCreateContent,
  BattleCreateHeadingContent,
  BattlePicksContent,
} from "@loot-os/design-system/templates/battles/battlesContent";
import { isLootApiErrorLike, useClientSeed } from "@loot-os/react";
import { useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import { spendableCents } from "../../../../lib/balance.js";
import { useCreateBattle } from "../../../../lib/battles.js";
import {
  battlePriceCents,
  expandRounds,
  initialBattlePicks,
} from "../../../../lib/battleTimeline.js";
import { useCurrency } from "../../../../lib/currency.js";
import { useDepositHref } from "../../../../lib/deposit.js";
import { pendingRevealFromError, useRevealClaim } from "../../../../lib/revealClaim.js";
import { useBalance } from "../../../../lib/shopper.js";
import { useTenant } from "../../../../lib/tenant.js";
import { useTerminology } from "../../../../lib/terminology.js";

type PlayerCount = (typeof BATTLE_PLAYER_COUNTS)[number];

function isPlayerCount(value: number): value is PlayerCount {
  return (BATTLE_PLAYER_COUNTS as readonly number[]).includes(value);
}

/** The page's words in the store's terms and currency, and the lobby's name it links back to. */
function useBattleCreateCopy(): { words: BattleCopy; copy: BattleCreateCopy } {
  const terms = useTerminology();
  const currency = useCurrency();
  return {
    words: defaultBattleCopy(terms),
    copy: defaultBattleCreateCopy({ terms, currencyPlural: currency.namePlural }),
  };
}

/** The page's head, the same before and after the boxes load. */
function battleCreateHeading(
  words: BattleCopy,
  heading: BattleCreateCopy["heading"],
): BattleCreateHeadingContent {
  const lobby = { label: words.lobby, href: "/battles" };
  return {
    trail: [lobby],
    current: heading.current,
    title: heading.title,
    description: heading.description,
    back: lobby,
  };
}

/** The picks' heading, with the battle's length so far. */
function picksHeading(
  picks: BattleCreateCopy["picks"],
  rounds: number,
): Omit<BattlePicksContent, "state"> {
  return { title: picks.title(rounds), hint: picks.hint };
}

/**
 * The page while GET /v1/boxes is on its way, or after it failed: the form's first picks come
 * from the boxes (`?boxId=`), so the form waits for them.
 */
export function useBattleCreatePendingContent(boxes: {
  isError: boolean;
  error: unknown;
}): BattleCreateContent {
  const { box } = useTerminology();
  const { words, copy } = useBattleCreateCopy();
  return {
    heading: battleCreateHeading(words, copy.heading),
    notice: null,
    picks: {
      ...picksHeading(copy.picks, 0),
      state: boxes.isError
        ? {
            status: "error",
            message:
              boxes.error instanceof Error && boxes.error.message
                ? boxes.error.message
                : `failed to load ${box.plural}`,
          }
        : { status: "loading" },
    },
    settings: null,
    entry: null,
  };
}

/**
 * POST /v1/battles: pick boxes (quantity = rounds of that box), order them, choose seats, mode
 * and switches. The entry is Σ price × quantity, checked against the spendable balance and sent
 * as `expectedPriceCents`; a `battle_price_mismatch` refetches the boxes, a `reveal_pending`
 * opens the claim prompt. The words are the store's default copy (`defaultBattleCreateCopy`); the
 * shortfall and the refusals are the platform's.
 */
export function useBattleCreateContent({
  boxes: boxList,
  initialBoxId,
  refetchBoxes,
}: {
  boxes: BoxSummary[];
  /** The box page's `?boxId=`: the picks start with one round of it. */
  initialBoxId: string | undefined;
  /** After a `battle_price_mismatch`: the list the entry was priced from is stale. */
  refetchBoxes: () => void;
}): BattleCreateContent {
  const currency = useCurrency();
  const unit = currency.namePlural;
  const { hasFeature } = useTenant();
  const terms = useTerminology();
  const { words, copy } = useBattleCreateCopy();
  const navigate = useNavigate();
  const { promptClaim } = useRevealClaim();
  const balance = useBalance().data ?? null;
  const [clientSeed] = useClientSeed({});
  const depositHref = useDepositHref();

  const [initialPicks] = useState(() => initialBattlePicks(boxList, initialBoxId));
  const [quantities, setQuantities] = useState<Record<string, number>>(initialPicks.quantities);
  const [order, setOrder] = useState<string[]>(initialPicks.order);
  const [playerCount, setPlayerCount] = useState<PlayerCount>(2);
  const [mode, setMode] = useState<BattleMode>("standard");
  const [isPublic, setIsPublic] = useState(true);
  const [fastSpin, setFastSpin] = useState(false);
  const [startWithBots, setStartWithBots] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = useCreateBattle();
  const boxIds = useMemo(() => expandRounds(order, quantities), [order, quantities]);
  const priceCents = battlePriceCents(boxList, quantities);
  const spendable = balance ? spendableCents(balance) : null;
  const short = spendable !== null && spendable < priceCents;
  const roundsLeft = MAX_BATTLE_ROUNDS - boxIds.length;
  const format = (cents: number) => formatAmountWithUnit(cents, currency);

  function setQuantity(box: BoxSummary, next: number) {
    const clamped = Math.max(0, Math.min(next, (quantities[box.id] ?? 0) + roundsLeft));
    setQuantities((prev) => ({ ...prev, [box.id]: clamped }));
    setOrder((prev) => {
      if (clamped === 0) return prev.filter((id) => id !== box.id);
      return prev.includes(box.id) ? prev : [...prev, box.id];
    });
  }

  function move(boxId: string, direction: -1 | 1) {
    setOrder((prev) => {
      const index = prev.indexOf(boxId);
      const target = index + direction;
      if (index === -1 || target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(index, 1);
      if (moved) next.splice(target, 0, moved);
      return next;
    });
  }

  function pick(box: BoxSummary): BattleBoxPickContent {
    const quantity = quantities[box.id] ?? 0;
    return {
      id: box.id,
      name: box.name,
      imageUrl: box.imageUrl,
      priceCents: box.priceCents,
      quantity,
      quantityLabel: copy.picks.pick.rounds(box.name),
      maxQuantity: quantity + Math.max(0, roundsLeft),
      onQuantityChange: (next) => setQuantity(box, next),
      decreaseLabel: copy.picks.pick.fewer(box.name),
      increaseLabel: copy.picks.pick.more(box.name),
    };
  }

  const picked: BattleBoxPickContent[] = order.flatMap((boxId, index) => {
    const box = boxList.find((b) => b.id === boxId);
    if (!box) return [];
    return [
      {
        ...pick(box),
        position: copy.picks.pick.position(index),
        earlier: {
          label: copy.picks.pick.earlier(box.name),
          onPress: () => move(boxId, -1),
          disabled: index === 0,
        },
        later: {
          label: copy.picks.pick.later(box.name),
          onPress: () => move(boxId, 1),
          disabled: index === order.length - 1,
        },
        remove: { label: copy.picks.pick.remove(box.name), onPress: () => setQuantity(box, 0) },
      },
    ];
  });

  function submit() {
    setError(null);
    create.mutate(
      {
        boxIds,
        playerCount,
        mode,
        isPublic,
        fastSpin,
        startWithBots,
        clientSeed,
        expectedPriceCents: priceCents,
      },
      {
        onSuccess: (res) =>
          void navigate({ to: "/battles/$battleId", params: { battleId: res.battle.id } }),
        onError: (err) => {
          if (isLootApiErrorLike(err) && err.code === "battle_price_mismatch") {
            refetchBoxes();
            setError("Prices changed — review the entry and try again.");
          } else if (isLootApiErrorLike(err) && err.code === "reveal_pending") {
            setError(null);
            promptClaim(pendingRevealFromError(err));
          } else {
            setError(
              err instanceof Error && err.message
                ? err.message
                : `could not create the ${terms.battle.singular}`,
            );
          }
        },
      },
    );
  }

  return {
    heading: battleCreateHeading(words, copy.heading),
    notice: error ? { message: error } : null,
    picks: {
      ...picksHeading(copy.picks, boxIds.length),
      state: {
        status: "ready",
        picks: picked,
        emptyMessage: copy.picks.empty,
        add: {
          label: copy.picks.add.label,
          title: copy.picks.add.title,
          description: copy.picks.add.description,
          doneLabel: copy.picks.add.done,
          boxes: boxList.map(pick),
        },
      },
    },
    settings: {
      players: {
        label: copy.settings.players,
        value: String(playerCount),
        options: BATTLE_PLAYER_COUNTS.map((count) => ({
          value: String(count),
          label: String(count),
        })),
        onValueChange: (value) => {
          const count = Number(value);
          if (isPlayerCount(count)) setPlayerCount(count);
        },
      },
      mode: {
        label: copy.settings.mode,
        value: mode,
        options: (["standard", "crazy"] as const).map((option) => ({
          value: option,
          label: BATTLE_MODE_COPY[option].label,
          description: BATTLE_MODE_COPY[option].rule,
        })),
        onValueChange: (value) => setMode(value === "crazy" ? "crazy" : "standard"),
      },
      toggles: [
        {
          id: "battle-private",
          ...copy.settings.toggles.private,
          checked: !isPublic,
          onCheckedChange: (checked) => setIsPublic(!checked),
        },
        {
          id: "battle-fast-spin",
          ...copy.settings.toggles.fastSpin,
          checked: fastSpin,
          onCheckedChange: setFastSpin,
        },
        {
          id: "battle-bots",
          ...copy.settings.toggles.startWithBots,
          checked: startWithBots,
          onCheckedChange: setStartWithBots,
        },
      ],
    },
    entry: {
      title: copy.entry.title,
      summary: copy.entry.summary(boxIds.length, playerCount),
      priceCents,
      balanceNote:
        spendable !== null
          ? spendableBalanceLine({
              spendable: format(spendable),
              held: balance && balance.heldCents > 0 ? format(balance.heldCents) : null,
            })
          : undefined,
      short: short
        ? {
            // A refusal, so the platform's words.
            message: `Not enough ${unit}.`,
            link: hasFeature("balancePurchases")
              ? { label: copy.entry.buy, href: depositHref }
              : undefined,
          }
        : undefined,
      submit: {
        label: create.isPending ? copy.entry.submitting : copy.entry.submit(format(priceCents)),
        disabled: boxIds.length === 0 || short || create.isPending,
        loading: create.isPending,
        onSubmit: submit,
      },
      cancel: { label: copy.entry.cancel, href: "/battles" },
    },
  };
}
